import React from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  Flex,
  FormControl,
  Grid,
  Input,
  Select,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { observer } from 'mobx-react';

const FieldLabel = ({ children }) => (
  <Text fontSize="xs" color="gray.400" mb={1}>{children}</Text>
);

const AdvancedSettings = observer(({ store }) => {
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Accordion allowToggle borderColor={borderColor}>
      <AccordionItem border="none">
        <AccordionButton px={0} py={2} _hover={{ bg: 'transparent' }}>
          <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="gray.400" flex={1} textAlign="left">
            Advanced Settings
          </Text>
          <AccordionIcon color="gray.400" />
        </AccordionButton>
        <AccordionPanel pb={4} px={0}>
          <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={4}>
            {/* Export mode */}
            <FormControl>
              <FieldLabel>Export mode</FieldLabel>
              <Select
                size="sm"
                value={store.exportMode}
                onChange={(e) => store.setOption('exportMode', e.target.value)}
              >
                <option value="crop">Crop to fill (center)</option>
                <option value="contain">Contain with padding</option>
                <option value="stretch">Stretch to fill</option>
              </Select>
            </FormControl>

            {/* Background color */}
            <FormControl>
              <FieldLabel>Background fill</FieldLabel>
              <Flex align="center" gap={2}>
                <Box
                  as="label"
                  w={8}
                  h={8}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.300"
                  overflow="hidden"
                  cursor="pointer"
                  flexShrink={0}
                  opacity={store.preserveTransparency && store.exportMode !== 'contain' ? 0.35 : 1}
                >
                  <Input
                    type="color"
                    value={store.bgColor}
                    onChange={(e) => store.setOption('bgColor', e.target.value)}
                    w="200%"
                    h="200%"
                    mt="-25%"
                    ml="-25%"
                    border="none"
                    p={0}
                    cursor="pointer"
                    isDisabled={store.preserveTransparency && store.exportMode !== 'contain'}
                  />
                </Box>
                <Text fontSize="xs" color="gray.500" fontFamily="mono">{store.bgColor}</Text>
              </Flex>
            </FormControl>

            {/* Padding slider */}
            <FormControl>
              <Flex justify="space-between" align="baseline" mb={1}>
                <FieldLabel>Padding</FieldLabel>
                <Text fontSize="xs" color="gray.400">{store.padding}%</Text>
              </Flex>
              <Slider
                value={store.padding}
                min={0}
                max={20}
                step={1}
                onChange={(v) => store.setOption('padding', v)}
              >
                <SliderTrack bg="gray.200"><SliderFilledTrack bg="blue.400" /></SliderTrack>
                <SliderThumb boxSize={3} />
              </Slider>
            </FormControl>

            {/* App name for asset catalogs */}
            <FormControl>
              <FieldLabel>Asset set name</FieldLabel>
              <Input
                size="sm"
                value={store.appName}
                onChange={(e) => store.setOption('appName', e.target.value)}
                placeholder="AppIcon"
              />
            </FormControl>

            {/* ZIP name */}
            <FormControl>
              <FieldLabel>ZIP filename</FieldLabel>
              <Input
                size="sm"
                value={store.zipName}
                onChange={(e) => store.setOption('zipName', e.target.value)}
                placeholder="app-icons"
              />
            </FormControl>
          </Grid>

          {/* Transparency toggle */}
          <Checkbox
            mt={4}
            size="sm"
            isChecked={store.preserveTransparency}
            onChange={(e) => store.setOption('preserveTransparency', e.target.checked)}
            colorScheme="blue"
          >
            <Text fontSize="xs">Preserve transparency (PNG alpha channel)</Text>
          </Checkbox>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
});

export default AdvancedSettings;
